import React, { useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash/fp";
import { SelectUserGroupsDialog } from "../../Dashboard/Tasks/SelectUserGroupsDialog";
import { Dialog, GridGenerator } from "@actinc/dls/components";
import { AlertContext } from "@actinc/dls/context";
import {
  Magnify as MagnifyIcon,
  OpenInNew as OpenInNewIcon,
} from "@actinc/dls/icons";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { createExternalReviewTaskMutation } from "../Items";
import {
  Box,
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  Paper,
  Tab,
  Typography,
} from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useClient, useMutation } from "urql";

import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";
import { ProgramsContext } from "../../../programsProvider";
import { UsersContext } from "../../../usersProvider";

import {
  AlertDialog,
  ControlledFormInput,
  ControlledFormSelect,
  DataGrid,
  ExpandMoreIcon,
  IconToolbar,
  PreviewContent,
  StandardsDialog,
} from "../../../components";

import {
  useGetMetadataConfigurationOptions,
  useStore,
} from "../../../services/hooks";
import {
  clearState,
  getState,
  removeState,
  setState,
} from "../../../services/sessionState";
import {
  filterOutSelectedPrograms,
  getSelectedItemOptionsFromMetadata,
  getOptionsByProgram,
  getProgramOptions,
  getUserOptions,
  PREVIEW_TYPES,
  setTitle,
} from "../../../services/utils";
import { CloneDialog } from "../CloneDialog";
import { MoveDialog } from "../MoveDialog";
import { RenameDialog } from "../RenameDialog";

const SearchPassages = () => {
  const client = useClient();

  const alertContext = useContext(AlertContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const programs = useContext(ProgramsContext);
  const users = useContext(UsersContext);
  const [selectUserDialogProps, setSelectUserDialogProps] = useState({
    open: false,
  });
  const [createExternalReviewTaskResponse, createExternalReviewTask] =
    useMutation(createExternalReviewTaskMutation);

  const { control, getValues, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues,
  });
  const { isSubmitting } = useFormState({ control });
  const selectedProgram = watch("program");

  const location = useLocation();
  const navigate = useNavigate();
  setState("lastSearchLocation")(location.pathname);

  const [dialogState, setDialogState] = useState({
    dialogName: "",
    open: false,
  });
  const datagridKey = "passageSearch";
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedTab, setSelectedTab] = useState("Metadata");
  const [standardsDialogProps, setStandardsDialogProps] = useState({
    open: false,
  });

  const [search, searchStore] = useStore("search");

  const [
    contentArea,
    developmentState,
    elementStatus,
    gradeLevel,
    language,
    release,
    restriction,
    type,
    unavailability,
  ] = useGetMetadataConfigurationOptions(
    metadataConfiguration,
    programs,
    "passage",
    [
      "contentArea",
      "developmentState",
      "elementStatus",
      "gradeLevel",
      "language",
      "release",
      "restriction",
      "type",
      "unavailability",
    ],
  );

  // Set the document title
  useEffect(() => {
    setTitle(["CDE", "Passage Search"]);
  }, []);

  useEffect(() => {
    if (!searchStore.client) searchStore.init(client);
  }, [client]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!loading) {
      if (
        (!location.state?.from ||
          [location.pathname, "/workspace"].includes(location.state?.from)) &&
        search?.type === "passage"
      ) {
        Object.keys(search.params).map((key) =>
          setValue(key, search.params[key]),
        );
      } else if (!_.isEmpty(search)) {
        searchStore.set({});
      } else if (getState("passageSearchFilters")) {
        const passageSearchFilters = getState("passageSearchFilters");
        if (passageSearchFilters.passagesList) {
          changeTab(null, "List");
          Object.keys(passageSearchFilters).map((key) =>
            setValue(key, passageSearchFilters[key]),
          );
          onListSearchSubmit();
        } else {
          changeTab(null, "Metadata");
          Object.keys(passageSearchFilters).map((key) =>
            setValue(key, passageSearchFilters[key]),
          );
          onMetadataSearchSubmit();
        }
        clearState();
      }
    }
    setLoading(false);
  }, [search]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const permittedActions = useMemo(
    () =>
      selectedRows
        .map((row) => row._actions)
        .reduce(
          (acc, actions) => _.intersection(acc)(actions),
          _.pipe([_.map((row) => row.name), _.uniq])(selectedRows).length === 1
            ? ["clone", "delete", "move", "createAdditionalTasks"]
            : ["delete", "move"],
        ),
    [selectedRows],
  );

  const programOptions = useMemo(
    () => getProgramOptions(programs)("searchContent"),
    [programs],
  );

  const selectedPassageProgramIds = selectedRows.map(
    (passage) => passage?.program,
  );

  const getFieldOptions = useMemo(() => {
    return getSelectedItemOptionsFromMetadata(programs)(
      selectedPassageProgramIds,
    )({
      developmentState,
      elementStatus,
      language,
      release,
      restriction,
      unavailability,
    });
  }, [
    developmentState,
    elementStatus,
    language,
    programs,
    release,
    restriction,
    selectedPassageProgramIds,
    unavailability,
  ]);

  const rows =
    (search?.response?.data &&
      search?.response?.data[`passagesBy${selectedTab}`]) ||
    [];

  const contentAreaOptions = useMemo(() => {
    const contentAreaValue = [...new Set(rows.map((row) => row.contentArea))]
      .filter(Boolean)
      .sort();
    return contentAreaValue;
  }, [rows]);

  const developmentStateOptions = useMemo(() => {
    const devStateValues = [...new Set(rows.map((row) => row.developmentState))]
      .filter(Boolean)
      .sort();
    return devStateValues;
  }, [rows]);

  if (!programOptions.length) return null;

  const changeTab = (e, tab) => {
    setSelectedTab(tab);
    reset({ ...defaultValues });
    searchStore.set({});
  };

  const closeDialog = () => {
    setDialogState({ open: false, dialogName: "" });
  };

  const deletePassage = async (val) => {
    closeDialog();

    // return if decision is to Cancel
    if (!val) return;

    try {
      setLoading(true);
      await searchStore.delete("passage")(deletePassagesMutation)({
        ids: selectedRows.map((passage) => passage?.id),
        objectType: "Passage",
        queryType: `passagesBy${selectedTab}`,
      });
      alertContext.actions.addAlert({
        message: `Passage${
          selectedRows.length > 1 ? "s" : ""
        } successfully deleted`,
        options: { variant: "success" },
      });
    } catch (err) {
      console.error("Delete Passage Failed:", err);
      throw new Error(err);
    } finally {
      setLoading(false);
    }
  };

  const getOriginalQuery = (string) => {
    switch (string) {
      case "searchPassagesByList":
        return searchPassagesByListQuery;
      case "searchPassagesByMetadata":
        return searchPassagesByMetadataQuery;
      default:
        break;
    }
  };

  const handleRefetch = async () => {
    const originalQueryName =
      search?.response?.operation?.query?.definitions[0]?.name?.value;

    setLoading(true);
    await searchStore.fetch("passage")(getOriginalQuery(originalQueryName))({
      ...search?.params,
      requestPolicy: "network-only",
    });
  };

  const handleSelectionModelChange = (data) => (selection) => {
    const selectedRows = data.filter(({ id }) => selection.includes(id));
    setSelectedRows(selectedRows);
  };

  const onListSearchSubmit = async () => {
    const passageList = [
      ...new Set(
        (getValues("passagesList") || "")
          .split(/[\n,]/)
          .map((name) => name.trim())
          .filter(Boolean),
      ),
    ];

    if (passageList.length) {
      setLoading(true);
      await searchStore.fetch("passage")(searchPassagesByListQuery)({
        passageList,
      });
      const results = searchStore
        .get()
        .response.data.passagesByList.map(({ name }) => name.toLowerCase());
      const passagesNotFound = passageList.filter(
        (name) => !results.includes(name.toLowerCase()),
      );
      if (passagesNotFound.length) {
        setDialogState({
          open: true,
          passagesNotFound,
          dialogName: "listSearch",
        });
      }
    }
  };

  const onMetadataSearchSubmit = async () => {
    const searchValues = getValues();
    setLoading(true);
    searchStore.fetch("passage")(searchPassagesByMetadataQuery)({
      ...searchValues,
      cdeID: parseInt(searchValues?.cdeID) || null,
      name: searchValues.name.trim(),
    });
  };

  const openCloneItemSetDialog = async () => {
    const contentSpecialistOptions = getUserOptions(users)({
      activeOnly: true,
      programs: selectedPassageProgramIds,
      roles: ["Content Specialist"],
    });
    const itemWriterOptions = getUserOptions(users)({
      activeOnly: true,
      programs: selectedPassageProgramIds,
      roles: ["Item Writer"],
    });

    setDialogState({
      contentArea: selectedRows[0]?.contentArea,
      dialogName: "clone",
      fieldOptions: {
        contentSpecialistOptions,
        itemWriterOptions,
        ...getFieldOptions,
        developmentState: [
          "Content Review 2",
          "Content Review 3",
          "Editorial Review",
          "Content Review 4",
          "Accessibility External",
          "Accessibility Internal",
        ].map((s) => ({ label: s, value: s })),
      },
      selectedRows,
      loading: true,
      mutation: cloneItemSetMutation,
      objectType: "passage",
      onClose: () => {
        closeDialog();
      },
      open: true,
      program: selectedPassageProgramIds[0],
    });
    await client
      .query(getPassageByIdQuery, { id: selectedRows[0].id })
      .toPromise()
      .then((res) => {
        const {
          data: {
            passageByID: { items },
          },
        } = res;
        setDialogState((prevState) => ({
          ...prevState,
          loading: false,
          itemSet: items,
        }));
      });
  };

  const openDeleteDialog = () => {
    setDialogState({
      confirmButtonText: "Delete",
      dialogName: "delete",
      message: (
        <Typography variant="subtitle1">
          {selectedRows.length > 1 ? (
            <>
              {`These ${selectedRows.length} passages
             will be permanently deleted`}
              {selectedRows.map((passage, idx) => (
                <Typography key={idx} sx={{ paddingLeft: 2 }} variant="body2">
                  {passage.name}
                </Typography>
              ))}
            </>
          ) : (
            `Passage ${selectedRows[0]?.name} will be permanently deleted`
          )}
        </Typography>
      ),
      onClose: deletePassage,
      open: true,
      title: `Confirm Passage${selectedRows.length > 1 ? "s" : ""} Deletion`,
    });
  };

  const openMovePassageDialog = () => {
    setDialogState({
      dialogName: "move",
      handleRefetch: () => {
        handleRefetch();
        closeDialog();
      },
      objectType: "passage",
      onClose: () => {
        closeDialog();
      },
      open: true,
      optionsToMove: selectedRows,
      programOptions: filterOutSelectedPrograms(programOptions)(selectedRows),
    });
  };

  const openAssignDialog = ({ users }) => {
    const selectedRowIds = selectedRows.map(({ id }) => id);
    const programs = [
      ...new Set(
        rows
          .filter(({ id }) => selectedRowIds.includes(id))
          .map(({ program }) => program),
      ),
    ];
    const userOptions = getUserOptions(users)({
      programs,
      roles: ["External Reviewer"],
    });

    setSelectUserDialogProps({
      details: ["dueDate", "instructions"],
      onClose: (details) => {
        setSelectUserDialogProps({ open: false });
        if (!details) return;
        const response = createExternalReviewTask({
          details: details,
          id: selectedRowIds,
          taskDefinition: "External Review",
          type: "passage",
        }).finally(() => {
          if (
            createExternalReviewTaskResponse &&
            !createExternalReviewTaskResponse.error
          ) {
            setLoading(false);
            alertContext.actions.addAlert({
              message: `External Reviewer assignment successfull!`,
              options: { variant: "success" },
            });
          } else {
            setLoading(false);
            alertContext.actions.addAlert({
              message: `External Reviewer assignment failed!`,
              options: { variant: "error" },
            });
          }
        });
      },
      open: true,
      programs,
      roles: ["External Reviewer"],
      users: userOptions,
    });
  };

  const openPassage = (newWindow) => (row) => () => {
    if (newWindow) {
      window.open(row.workspace.uri, row.workspace.name);
    } else {
      navigate(row.workspace.uri, { reloadDocument: true });
    }
  };

  const openRenameDialog = (selectedRow) => (e) => {
    setDialogState({
      currentName: selectedRow?.name,
      dialogName: "rename",
      id: selectedRow?.id,
      onClose: () => {
        closeDialog();
      },
      objectType: "passage",
      open: true,
      handleRefetch,
    });
  };

  const openStandardsDialog = () => {
    setStandardsDialogProps({
      action: "search",
      defaultValues: { standards: getValues("standards") },
      onClose: (standards = []) => {
        setValue("standards", standards);
        setStandardsDialogProps({ open: false });
      },
      open: true,
      selectedStandards: [],
    });
  };

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Passage Name",
      minWidth: 200,
      renderCell: (params) => {
        return (
          <>
            <Link
              component={RouterLink}
              disabled={!params?.row._actions.includes("view")}
              onClick={() => setState("passageSearchFilters")(getValues())}
              reloadDocument
              to={params?.row.workspace.uri}
              underline="always"
            >
              {params.value}
            </Link>
            <IconButton
              color="secondary"
              component={RouterLink}
              disabled={!params?.row._actions.includes("view")}
              onClick={() => setState("passageSearchFilters")(getValues())}
              size="small"
              target={params?.row.workspace.name}
              to={params?.row.workspace.uri}
            >
              <OpenInNewIcon />
            </IconButton>
            <PreviewContent
              objects={[
                { id: params?.row.id, versionID: params?.row.versionID },
              ]}
              objectType="Passage"
              previewTypes={PREVIEW_TYPES}
            />
          </>
        );
      },
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "programName",
      headerName: "Program",
      minWidth: 200,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "singleSelect",
      valueGetter: getProgramName(programs),
      valueOptions: programOptions.map(({ label }) => label),
    },
    {
      field: "type",
      headerName: "Passage Type",
      minWidth: 200,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getNonNullValue("type"),
    },
    {
      field: "contentSpecialist",
      headerName: " Content Specialist",
      minWidth: 200,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getContentSpecialistName(users),
    },
    {
      field: "contentArea",
      headerName: "Content Area",
      minWidth: 200,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "singleSelect",
      valueGetter: getNonNullValue("contentArea"),
      valueOptions: contentAreaOptions,
    },
    {
      field: "gradeLevel",
      headerName: "Grade",
      minWidth: 105,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
      valueGetter: getNonNullValue("gradeLevel"),
    },
    {
      field: "developmentState",
      headerName: "Development State",
      minWidth: 225,
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "singleSelect",
      valueGetter: getNonNullValue("developmentState"),
      valueOptions: developmentStateOptions,
    },
  ];

  const dataGridProps = {
    actions: [
      {
        disabled: (row) => !row?._actions?.includes("view"),
        label: "Open",
        onClick: openPassage(false),
      },
      {
        disabled: (row) => !row?._actions?.includes("view"),
        label: "Open in New Window",
        onClick: openPassage(true),
      },
      {
        disabled: (row) => !row?._actions?.includes("rename"),
        label: "Rename",
        onClick: openRenameDialog,
      },
    ],
    checkboxSelection: rows
      .map((passage) => passage?.[`_actions`])
      .flat()
      .some((action) => ["delete", "move"].includes(action)),
    columns,
    datagridKey,
    disableRowSelectionOnClick: true,
    error: search?.response?.error?.message,
    isRowSelectable: ({ row }) => {
      return row?.["_actions"].some((action) =>
        ["delete", "move"].includes(action),
      );
    },
    loading,
    onRowSelectionModelChange: handleSelectionModelChange(rows),
    rows,
    sortModel:
      selectedTab === "Metadata"
        ? [
            {
              field: "name",
              sort: "asc",
            },
          ]
        : [],
  };

  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "xs",
    onClose: closeDialog,
    open: dialogState.open && dialogState.dialogName === "listSearch",
    scroll: "paper",
    title: "Passages Not Found",
  };

  const rowLimitOptions = [100, 500, 1000].map((value) => ({
    label: value,
    value,
  }));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: 2,
        }}
      >
        <Typography variant="h4">Passages</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <TabContext value={selectedTab}>
          <TabList onChange={changeTab}>
            <Tab key="Metadata" label="Metadata" value="Metadata" />
            <Tab key="List" label="List" value="List" />
          </TabList>
          <TabPanel
            key="Metadata"
            index="0"
            sx={{ overflowY: "auto" }}
            value="Metadata"
          >
            <Paper
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: 1,
                padding: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSubmit(onMetadataSearchSubmit)}>
                  <Box
                    sx={{
                      alignItems: "end",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <GridGenerator
                      groups={[
                        [
                          {
                            children: (
                              <ControlledFormSelect
                                control={control}
                                fullWidth={true}
                                id={`passageSearch-program`}
                                label="Program"
                                name="program"
                                options={programOptions}
                              />
                            ),
                            key: `passageSearch-program`,
                          },
                          {
                            children: (
                              <ControlledFormInput
                                control={control}
                                fullWidth={true}
                                id={`passageSearch-name`}
                                label="Passage Name"
                                name="name"
                              />
                            ),
                            key: `passageSearch-name`,
                          },
                          {
                            children: (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                }}
                              >
                                <ControlledFormSelect
                                  control={control}
                                  id={`passageSearch-rowLimit`}
                                  label="Number of Results"
                                  name="rowLimit"
                                  options={rowLimitOptions}
                                />
                                <Box sx={{ marginBottom: 2 }}>
                                  <ExpandMoreIcon
                                    expand={expanded}
                                    onClick={() => setExpanded(!expanded)}
                                  />
                                </Box>
                              </Box>
                            ),
                            key: `passageSearch-rowLimit`,
                          },
                        ],
                      ]}
                    />
                    <Collapse
                      aria-label={`collapsable panel ${
                        expanded ? "expanded" : "collapsed"
                      }`}
                      in={expanded}
                      sx={{ width: "100%" }}
                      timeout="auto"
                    >
                      <GridGenerator
                        groups={[
                          [
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  fullWidth={true}
                                  id={`passageSearch-cdeID`}
                                  label="Passage ID"
                                  name="cdeID"
                                  rules={{
                                    pattern: {
                                      message:
                                        "This field must contain a valid number",
                                      value: /^\d+$/,
                                    },
                                  }}
                                />
                              ),
                              key: `passageSearch-passageName`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`passageSearch-developmentState`}
                                  label="Development State"
                                  name="developmentState"
                                  options={getOptionsByProgram(programs)(
                                    selectedProgram,
                                  )(developmentState)}
                                />
                              ),
                              key: `passageSearch-developmentState`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`passageSearch-contentSpecialist`}
                                  label="Content Specialist"
                                  name="contentSpecialist"
                                  options={getUserOptions(users)({
                                    activeOnly: false,
                                    programs: [selectedProgram],
                                    roles: ["Content Specialist"],
                                  })}
                                />
                              ),
                              key: `passageSearch-contentSpecialist`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`passageSearch-itemWriter`}
                                  label="Item Writer"
                                  name="itemWriter"
                                  options={getUserOptions(users)({
                                    activeOnly: false,
                                    programs: [selectedProgram],
                                    roles: ["Item Writer"],
                                  })}
                                />
                              ),
                              key: `passageSearch-itemWriter`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  fullWidth={true}
                                  disabled={!selectedProgram}
                                  id={`passageSearch-type`}
                                  label="Passage Type"
                                  name="type"
                                  options={getOptionsByProgram(programs)(
                                    selectedProgram,
                                  )(type)}
                                />
                              ),
                              key: `passageSearch-type`,
                            },
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`passageSearch-gradeLevel`}
                                  label="Grade Level"
                                  name="gradeLevel"
                                  options={getOptionsByProgram(programs)(
                                    selectedProgram,
                                  )(gradeLevel)}
                                />
                              ),
                              key: `passageSearch-gradeLevel`,
                            },
                          ],
                          [
                            {
                              children: (
                                <ControlledFormSelect
                                  control={control}
                                  disabled={!selectedProgram}
                                  fullWidth={true}
                                  id={`passageSearch-contentArea`}
                                  label="Content Area"
                                  name="contentArea"
                                  options={getOptionsByProgram(programs)(
                                    selectedProgram,
                                  )(contentArea)}
                                />
                              ),
                              key: `passageSearch-contentArea`,
                            },
                            {
                              children: (
                                <ControlledFormInput
                                  control={control}
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="open standards dialog"
                                        edge="end"
                                        onClick={openStandardsDialog}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        <MagnifyIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  }
                                  fullWidth={true}
                                  id={`passageSearch-standards`}
                                  label="Standards"
                                  name="standards"
                                />
                              ),
                              key: `passageSearch-standards`,
                            },
                            {
                              children: <Box />,
                              key: `passageSearch-blank1`,
                            },
                          ],
                        ]}
                      />
                    </Collapse>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Button
                        onClick={() => {
                          reset({ ...defaultValues });
                          removeState("passageSearchFilters");
                          setSelectedRows([]);
                          searchStore.set({});
                        }}
                        sx={{ marginRight: 2 }}
                        variant="outlined"
                      >
                        Reset
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        sx={{ marginRight: 2 }}
                        type="submit"
                        variant="contained"
                      >
                        Search
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </TabPanel>
          <TabPanel
            index="1"
            key="List"
            sx={{ overflowY: "auto" }}
            value="List"
          >
            <Paper
              elevation={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                margin: 1,
                padding: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSubmit(onListSearchSubmit)}>
                  <Box>
                    <GridGenerator
                      groups={[
                        [
                          {
                            children: (
                              <ControlledFormInput
                                control={control}
                                fullWidth={true}
                                inputProps={{
                                  maxRows: 10,
                                  minRows: 10,
                                }}
                                label="Passage Names"
                                multiline={true}
                                name="passagesList"
                              />
                            ),
                            key: `passageSearch-list`,
                          },
                        ],
                      ]}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "end",
                      }}
                    >
                      <Button
                        onClick={() => {
                          reset({ ...defaultValues });
                          searchStore.set({});
                          removeState("passageSearchFilters");
                        }}
                        sx={{ marginRight: 2 }}
                        variant="outlined"
                      >
                        Reset
                      </Button>
                      <Button
                        disabled={isSubmitting}
                        sx={{ marginRight: 2 }}
                        type="submit"
                        variant="contained"
                      >
                        Search
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </TabPanel>
        </TabContext>
      </Box>
      {(search?.response || loading) && (
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            margin: 1,
          }}
        >
          {!!selectedRows.length && (
            <IconToolbar
              handleClone={openCloneItemSetDialog}
              handleDelete={openDeleteDialog}
              handleMove={openMovePassageDialog}
              handleAssignUser={() =>
                openAssignDialog({
                  users: users,
                })
              }
              objectType="Passage"
              permittedActions={permittedActions}
              selectedRows={selectedRows}
            ></IconToolbar>
          )}
          <Box sx={{ flexGrow: 1, minHeight: "500px" }}>
            <DataGrid {...dataGridProps}></DataGrid>
          </Box>
        </Paper>
      )}
      <Dialog {...dialogProps}>
        <DialogContent>
          <Typography variant="subtitle2">
            No results matched the following passage
            {!!dialogState?.passagesNotFound &&
            dialogState.passagesNotFound.length > 1
              ? "s"
              : ""}
            :
          </Typography>
          <List dense={true} disablePadding={true}>
            {!!dialogState?.passagesNotFound &&
              dialogState.passagesNotFound.map((passageName) => (
                <ListItem key={passageName}>{passageName}</ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {dialogState.open && dialogState.dialogName === "delete" && (
        <AlertDialog {...dialogState} />
      )}
      {dialogState.open && dialogState.dialogName === "clone" && (
        <CloneDialog {...dialogState} />
      )}
      {dialogState.open && dialogState.dialogName === "rename" && (
        <RenameDialog {...dialogState} />
      )}
      {dialogState.open && dialogState.dialogName === "move" && (
        <MoveDialog {...dialogState} />
      )}
      {standardsDialogProps.open && (
        <StandardsDialog {...standardsDialogProps} />
      )}
      {selectUserDialogProps.open && (
        <SelectUserGroupsDialog {...selectUserDialogProps} />
      )}
    </>
  );
};

const cloneItemSetMutation = `
  mutation CloneItemSet($cloneRelationships: Boolean!, $data: PassageInput!, $itemsToClone: [ID!]!, $newPassageName: String!, $passageToClone: ID!, $relationshipTypeToClone: RelationshipTypeEnum, $ttsGenerate: Boolean!, $convertIPR: Boolean) {
    cloneItemSet(cloneRelationships: $cloneRelationships,  data: $data, itemsToClone: $itemsToClone, newPassageName: $newPassageName, passageToClone: $passageToClone, relationshipTypeToClone: $relationshipTypeToClone, ttsGenerate: $ttsGenerate, convertIPR: $convertIPR) {
      items {
        contentArea
        contentSpecialist {
          id
        }
        developmentState
        id
        itemWriter {
          id
        }
        name
        program
        versionID
      }
      passage {
        contentArea
        contentSpecialist {
          id
        }
        developmentState
        id
        itemWriter {
          id
        }
        name
        program
      }
    }
  }
`;

const getPassageByIdQuery = `
  query getPassageByID($id: ID!, $versionID: String) {
    passageByID(id: $id, versionID: $versionID) {
      id
      items {
        description
        id
        name
        program
        versionID
      }
      program
    }
  }
`;

const searchPassagesByMetadataQuery = `
  query searchPassagesByMetadata($cdeID: Int, $contentArea: String, $contentSpecialist: String, $developmentState: String, $gradeLevel: String, $itemWriter: String, $name: String, $program: ID, $rowLimit: Int, $standards: [String!], $type: String) {
    passagesByMetadata(cdeID: $cdeID, contentArea: $contentArea, contentSpecialist: $contentSpecialist, developmentState: $developmentState, gradeLevel: $gradeLevel, itemWriter: $itemWriter, name: $name, program: $program, rowLimit: $rowLimit, standards: $standards, type: $type) {
      id
      contentSpecialist {
        id
        name
      }
      contentArea
      developmentState
      gradeLevel
      hasItems
      name
      program
      associatedItemIDs
      type
      versionID
      _actions
    }
  }`;

const searchPassagesByListQuery = `
  query searchPassagesByList($passageList: [String!]!) {
    passagesByList(passageList: $passageList) {
      id
      contentSpecialist {
        id
        name
      }
      contentArea
      developmentState
      gradeLevel
      hasItems
      name
      program
      type
      versionID
      _actions
    }
  }`;

const deletePassagesMutation = `
    mutation DeletePassages($ids: [ID!]!, $objectType: ObjectTypeEnum!) {
      deletePassages(ids: $ids, objectType: $objectType) {
        id
      }
    }
  `;

const defaultValues = {
  cdeID: "",
  contentArea: null,
  contentSpecialist: null,
  developmentState: null,
  gradeLevel: null,
  itemWriter: null,
  name: "",
  program: null,
  rowLimit: 100,
  standards: [],
  type: null,
};
const getNonNullValue = (fieldName) => (params) =>
  `${params.row?.[fieldName] || ""}`;
const getProgramName = (programs) => (params) =>
  `${programs.find(({ id }) => id === params.row?.program)?.name || ""}`;
const getContentSpecialistName = (users) => (params) =>
  `${
    users.find(({ id }) => id === params.row?.contentSpecialist?.id)?.name || ""
  }`;

export {
  getPassageByIdQuery,
  deletePassagesMutation,
  SearchPassages,
  searchPassagesByListQuery,
  searchPassagesByMetadataQuery,
  getProgramName,
};
